import type { MeshGeometry } from "../engine/mesh.js";

export type CombatPoint = { x: number; y: number; z: number };
export type CombatTint = readonly [number, number, number, number];
export interface MachinePose extends CombatPoint {
  yaw: number; turretYaw: number; aimPitch: number; speed: number;
  phase: number; collapse: number; recoil: number; damaged: number;
  state?: number; steering?: number; damageStage?: number; damageZone?: number; damageSeed?: number;
  hullPitch?: number; hullRoll?: number; hitResponse?: number; cannonRecoil?: number;
}
export interface FootPose extends CombatPoint { ground: number; sampleAt: number }
export const MACHINE_PART_CAPACITY = 160;
const STEEL: CombatTint = [.22, .25, .22, 1];
const EDGE: CombatTint = [.36, .37, .29, 1];
const DARK: CombatTint = [.075, .09, .08, 1];
const RUST: CombatTint = [.28, .17, .10, 1];
const WARDEN: CombatTint = [.40, .35, .24, 1];
const WARNING: CombatTint = [.66, .44, .12, 1];
const GREEN: CombatTint = [.16, .55, .24, 1];
const RED: CombatTint = [.9, .045, .018, 1];
const ROVER: CombatTint = [.31, .32, .23, 1];
const PATCH: CombatTint = [.39, .27, .17, 1];
const AMBER: CombatTint = [1, .35, .055, 1];

/** All actors share these two primitive resources; articulation is instance data. */
export function machineBox(): MeshGeometry {
  const positions: number[] = [], normals: number[] = [], indices: number[] = [];
  const corners = [[-.5, -.5, -.5], [.5, -.5, -.5], [.5, .5, -.5], [-.5, .5, -.5],
    [-.5, -.5, .5], [.5, -.5, .5], [.5, .5, .5], [-.5, .5, .5]];
  for (const [face, normal] of [ [[0, 3, 2, 1], [0, 0, -1]], [[4, 5, 6, 7], [0, 0, 1]],
    [[0, 4, 7, 3], [-1, 0, 0]], [[1, 2, 6, 5], [1, 0, 0]],
    [[3, 7, 6, 2], [0, 1, 0]], [[0, 1, 5, 4], [0, -1, 0]] ] as const) {
    const base = positions.length / 3;
    for (const index of face) { positions.push(...corners[index]!); normals.push(...normal); }
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  return { positions: new Float32Array(positions), normals: new Float32Array(normals), indices: new Uint16Array(indices) };
}

/** Octagonal industrial wheel, radius .5, axle along local X. */
export function machineWheel(): MeshGeometry {
  const positions: number[] = [], normals: number[] = [], indices: number[] = [];
  for (let side = 0; side < 8; side++) {
    const a = side * Math.PI / 4, b = (side + 1) * Math.PI / 4, mid = (a + b) / 2;
    const base = positions.length / 3;
    for (const [x, angle] of [[-.5, a], [.5, a], [.5, b], [-.5, b]]) {
      positions.push(x!, Math.cos(angle!) * .5, Math.sin(angle!) * .5);
      normals.push(0, Math.cos(mid), Math.sin(mid));
    }
    indices.push(base, base + 2, base + 1, base, base + 3, base + 2);
    for (const x of [-.5, .5]) {
      const end = positions.length / 3;
      positions.push(x, 0, 0, x, Math.cos(a) * .5, Math.sin(a) * .5, x, Math.cos(b) * .5, Math.sin(b) * .5);
      for (let i = 0; i < 3; i++) normals.push(Math.sign(x), 0, 0);
      if (x < 0) indices.push(end, end + 2, end + 1); else indices.push(end, end + 1, end + 2);
    }
  }
  return { positions: new Float32Array(positions), normals: new Float32Array(normals), indices: new Uint16Array(indices) };
}

export class MachineParts {
  readonly metal = new Float32Array(MACHINE_PART_CAPACITY * 20);
  readonly glow = new Float32Array(12 * 20);
  readonly wheels = new Float32Array(16 * 20);
  metalCount = 0; glowCount = 0; wheelCount = 0;
  readonly feet: FootPose[] = Array.from({ length: 4 }, () => ({ x: 0, y: 0, z: 0, ground: 0, sampleAt: -Infinity }));
  private readonly a: CombatPoint = { x: 0, y: 0, z: 0 };
  private readonly b: CombatPoint = { x: 0, y: 0, z: 0 };
  private readonly knee: CombatPoint = { x: 0, y: 0, z: 0 };

  build(type: number, pose: MachinePose, heightAt: (x: number, z: number) => number, now: number): void {
    this.metalCount = 0; this.glowCount = 0; this.wheelCount = 0;
    if (type === 0) this.hound(pose, heightAt, now);
    else if (type === 1) this.bastion(pose);
    else this.technical(pose, heightAt, now);
  }

  private hound(p: MachinePose, heightAt: (x: number, z: number) => number, now: number): void {
    const bodyY = 1.0 - p.collapse * .61 + Math.sin(p.phase * 2) * Math.min(.045, p.speed * .012) * (1 - p.collapse);
    const armor: CombatTint = p.collapse > .5 ? DARK : STEEL;
    this.box(p, 0, bodyY, -.08, .64, .43, 1.58, armor);
    this.box(p, 0, bodyY + .25, -.28, .50, .15, 1.08, EDGE);
    this.box(p, 0, bodyY - .20, -.25, .35, .15, 1.1, DARK);
    this.box(p, -.36, bodyY + .025, -.32, .12, .25, .91, RUST);
    this.box(p, .36, bodyY + .025, -.32, .12, .25, .91, RUST);
    for (const x of [-.22, .22]) this.box(p, x, bodyY + .30, -.5, .10, .06, .64, DARK);
    // Observation is a small articulated optic; the ballistic gun remains a distinct part.
    const sensor = houndSensorPresentation(p, now);
    this.localPoint(p, 0, bodyY + .16, .62, this.a);
    const head = { ...p, ...this.a, yaw: sensor.yaw, hullPitch: 0, hullRoll: 0 };
    this.box(head, 0, 0, .03, .49, .32, .50, armor, -sensor.pitch);
    this.box(head, 0, .035, .31, .32, .14, .12, DARK, -sensor.pitch);
    this.box(head, -.075, .05, .38, .065, .06, .025, sensor.led, -sensor.pitch, sensor.lit);
    this.box(head, .075, .05, .38, .10, .07, .025, DARK, -sensor.pitch);
    this.box(head, .075, .05, .395, .055, .04, .012, GREEN, -sensor.pitch, sensor.state === "SEARCHING" && sensor.lit);
    this.localPoint(p, 0, bodyY + .08, .64, this.a);
    const gun = { ...p, ...this.a, yaw: p.turretYaw, hullPitch: 0, hullRoll: 0 };
    this.box(gun, .22, 0, .03, .21, .19, .34, EDGE, -p.aimPitch);
    this.box(gun, .22, Math.sin(p.aimPitch) * .32, Math.cos(p.aimPitch) * .32 - p.recoil * .06, .10, .10, .46, DARK, -p.aimPitch);
    this.box(gun, .22, Math.sin(p.aimPitch) * .515, Math.cos(p.aimPitch) * .515 - p.recoil * .06, .15, .14, .09, EDGE, -p.aimPitch);
    this.box(gun, .22, Math.sin(p.aimPitch) * .565, Math.cos(p.aimPitch) * .565 - p.recoil * .06, .075, .07, .012, DARK, -p.aimPitch);
    this.box(p, 0, bodyY + .37, -.63, .07, .24, .07, DARK);
    for (let leg = 0; leg < 4; leg++) {
      const side = leg % 2 === 0 ? -1 : 1, front = leg < 2;
      const phase = p.phase + (leg === 0 || leg === 3 ? 0 : Math.PI);
      const moving = Math.min(1, p.speed / .9) * (1 - p.collapse);
      const stride = Math.cos(phase) * Math.min(.45, .15 + p.speed * .055) * moving;
      const foot = this.feet[leg]!;
      this.localPoint(p, side * (.44 + p.collapse * .20), 0, (front ? .57 : -.62) + stride, foot);
      if (now - foot.sampleAt >= 100 || !Number.isFinite(foot.sampleAt)) {
        const ground = heightAt(foot.x, foot.z);
        foot.ground = Number.isFinite(ground) ? ground : p.y;
        foot.sampleAt = now;
      }
      foot.y = foot.ground + .055 + Math.max(0, Math.sin(phase)) * .27 * moving;
      this.localPoint(p, side * .34, bodyY - .03, front ? .54 : -.6, this.a);
      this.knee.x = (this.a.x + foot.x) / 2 + Math.sin(p.yaw) * (front ? -.22 : .24);
      this.knee.z = (this.a.z + foot.z) / 2 + Math.cos(p.yaw) * (front ? -.22 : .24);
      this.knee.y = (this.a.y + foot.y) / 2 + .06 * (1 - p.collapse);
      this.segment(this.a, this.knee, .16, .19, armor);
      this.segment(this.knee, foot, .105, .12, EDGE);
      this.worldBox(this.knee.x, this.knee.y, this.knee.z, .20, .20, .20, p.yaw, 0, DARK);
      this.worldBox(foot.x, foot.y, foot.z, .20, .10, .28, p.yaw, 0, DARK);
    }
  }

  private bastion(p: MachinePose): void {
    const armor = p.collapse > .5 ? DARK : WARDEN;
    const hull = { ...p, y: p.y - p.collapse * .14, hullPitch: (p.hullPitch ?? 0) + (p.cannonRecoil ?? 0) * .015 };
    // Recompose the hull/track/turret separately: a wide, low 7.6m tracked chassis.
    this.box(hull, 0, 1.08, -.15, 2.87, 1.03, 6.80, armor);
    this.box(hull, 0, 1.67, -.30, 2.67, .25, 5.65, EDGE);
    this.box(hull, 0, 1.17, 3.15, 2.82, .77, .61, armor, -.27);
    this.box(hull, 0, .96, -3.53, 2.80, .76, .36, DARK);
    for (const side of [-1, 1]) {
      this.box(hull, side * 1.54, .67, -.03, .84, 1.17, 7.33, DARK);
      this.box(hull, side * 1.53, 1.35, -.03, .89, .18, 7.54, armor);
      for (let wheel = 0; wheel < 7; wheel++) {
        this.localPoint(hull, side * 1.93, .66, -2.99 + wheel * .995, this.a);
        this.addWheel(this.a, .09, .99, .99, p.yaw, p.phase, wheel % 2 ? EDGE : RUST);
      }
      const travel = ((p.phase * .49) % .66 + .66) % .66;
      for (let plate = 0; plate < 11; plate++) {
        this.box(hull, side * 1.54, .11, -3.61 + plate * .66 + travel, .84, .12, .19, EDGE);
        this.box(hull, side * 1.54, 1.21, 3.61 - plate * .66 - travel, .84, .12, .19, EDGE);
      }
      for (let panel = 0; panel < 4; panel++) this.box(hull, side * 1.95, 1.03, -2.7 + panel * 1.73, .045, .62, 1.48, armor);
      for (let vent = 0; vent < 5; vent++) this.box(hull, side * .86, 1.82, -2.0 + vent * .22, .57, .04, .085, DARK);
      this.box(hull, side * 1.03, 1.3, 3.5, .26, .18, .08, p.collapse > .5 ? DARK : AMBER, 0, p.collapse < .5);
    }
    const turret = { ...p, y: p.y + 2.13 - p.collapse * .17, yaw: p.turretYaw, hullPitch: 0, hullRoll: 0 };
    this.box(turret, 0, -.26, -.18, 1.87, .34, 2.00, DARK);
    this.box(turret, 0, .08, -.25, 2.38, .69, 2.60, armor);
    this.box(turret, 0, .52, -.48, 1.94, .18, 2.02, EDGE);
    this.box(turret, 0, .05, 1.16, .71, .62, .77, armor, -p.aimPitch);
    const recoil = (p.cannonRecoil ?? 0) * .47;
    this.aimedBox(turret, 0, .03, 1.25, 1.59 - recoil, .27, .27, 3.18, DARK, p.aimPitch);
    this.aimedBox(turret, 0, .03, 1.25, 3.14 - recoil, .43, .37, .22, EDGE, p.aimPitch);
    this.aimedBox(turret, 0, .03, 1.25, 3.26 - recoil, .25, .21, .02, DARK, p.aimPitch);
    // Separate industrial rocket pod and secondary gun; emitter positions mirror shared geometry.
    this.aimedBox(turret, -1.05, .50, 0, .92, .62, .49, 1.77, DARK, p.aimPitch);
    this.aimedBox(turret, -1.05, .74, 0, .88, .67, .10, 1.71, WARNING, p.aimPitch);
    this.aimedBox(turret, .95, .50, 0, 1.30 - p.recoil * .08, .17, .17, 1.9, DARK, p.aimPitch);
    this.aimedBox(turret, .95, .50, 0, 2.20 - p.recoil * .08, .25, .24, .10, EDGE, p.aimPitch);
    this.box(turret, 0, .68, -.27, .51, .16, .41, DARK);
    this.box(turret, 0, .68, -.05, .15, .055, .025, p.collapse > .5 ? DARK : AMBER, 0, p.collapse < .5);
    this.box(turret, .78, .77, -1.01, .045, .60, .045, DARK);
  }

  private technical(p: MachinePose, heightAt: (x: number, z: number) => number, now: number): void {
    const stage = p.damageStage ?? 0, zone = p.damageZone ?? 0, hit = p.hitResponse ?? 0;
    const front = zone === 1 ? stage : 0, rear = zone === 4 ? stage : 0;
    const steel = p.collapse > .5 ? DARK : ROVER;
    const hull = { ...p, y: p.y - p.collapse * .13 - hit * .055 };
    const bend = ((p.damageSeed ?? 1) % 17 / 17 - .5) * .10;
    const crumpledSide = (p.damageSeed ?? 1) % 2 ? -1 : 1;
    // Open cab with thin pillars makes the driver visible from front and sides.
    this.box(hull, 0, .62, -.08, 1.55, .27, 4.90, DARK);
    this.box(hull, 0, .88, .35, 1.91, .20, 2.85, steel);
    if (front < 2) {
      this.box(hull, 0, 1.10 + front * .045, 1.77 - front * .055, 1.87, .34 - front * .035, 1.61 - front * .10, steel, front * .07 + bend * front);
      this.box(hull, 0, .77 + front * .035, 2.60 - front * .105, 2.03, .20, .20, EDGE, front * .09);
    } else {
      // Two buckled plates expose the dark engine bay and break the formerly flat hood silhouette.
      const buckle = front === 2 ? .48 : .86, lift = front === 2 ? .20 : .36;
      this.box(hull, 0, 1.10, 1.65, 1.68, .25, 1.54, DARK);
      this.box(hull, 0, 1.26 + lift, 2.01 - front * .04, 1.79, .105, .76, steel, buckle, false, bend * front);
      this.box(hull, crumpledSide * .055, 1.26 + lift, 1.29 - front * .02, 1.79, .105, .76, PATCH, -buckle, false, -bend * front);
      for (const side of [-1, 1]) {
        const droop = side === crumpledSide ? (front === 2 ? .20 : .42) : .06;
        this.box(hull, side * .51, .77 - droop, 2.60 - front * .12, .99, .17, .20, EDGE,
          front * .09, false, side * (side === crumpledSide ? .25 + (front - 2) * .25 : .09), side * .07);
      }
      this.box(hull, -.24, 1.30, 1.65, .45, .14, .83, RUST);
      this.box(hull, .34, 1.24, 1.76, .28, .12, .67, EDGE);
    }
    this.box(hull, 0, 1.00, 2.52 - front * .09, 1.00, .17, .06, DARK);
    for (let grille = 0; grille < 5; grille++) this.box(hull, -.42 + grille * .21, 1.01, 2.56 - front * .09, .035, .13, .03, EDGE);
    this.box(hull, 0, 1.97 - stage * .012, .40, 1.98, .08, 1.60, steel);
    this.box(hull, 0, 1.31, 1.12, 1.75, .19, .23, DARK);
    this.box(hull, 0, .84, -1.63, 1.84, .15, 2.06, PATCH);
    for (let rib = 0; rib < 6; rib++) this.box(hull, -.76 + rib * .30, .93, -1.65, .025, .025, 1.9, EDGE);
    this.box(hull, rear >= 2 ? crumpledSide * .11 : 0, 1.16 - rear * .095, -2.60 + rear * .025, 1.92, .52, .13, steel,
      rear >= 2 ? -.64 - (rear - 2) * .40 : rear * -.14, false, rear >= 2 ? crumpledSide * (.15 + (rear - 2) * .12) : 0, rear >= 2 ? crumpledSide * .14 : 0);
    this.box(hull, rear >= 2 ? crumpledSide * .08 : 0, .71 - rear * .045, -2.65 + rear * .045, 2.01, .17, .15, EDGE,
      0, false, rear >= 2 ? crumpledSide * .10 : 0);
    for (const side of [-1, 1]) {
      const sideDamage = zone === (side < 0 ? 2 : 3) ? stage : 0;
      const peeled = sideDamage >= 2 ? sideDamage - 1 : 0;
      if (peeled) this.box(hull, side * .94, 1.12, .33, .045, .43, 1.40, DARK);
      this.box(hull, side * (.945 + (peeled ? .20 + (peeled - 1) * .16 : sideDamage * .022)), 1.20 - sideDamage * .075, .33,
        .10, .53, 1.51 - sideDamage * .04, steel, sideDamage * .065, false, side * peeled * .23, side * peeled * .22);
      this.box(hull, side * .92, 1.64, 1.13, .075, .62, .09, EDGE, -.16);
      this.box(hull, side * .93, 1.60, -.36, .08, .68, .09, EDGE);
      this.box(hull, side * (.95 + sideDamage * .04), 1.18 - sideDamage * .045, -1.60, .10, .53, 1.99, steel,
        sideDamage * -.065, false, side * peeled * -.14, side * peeled * -.055);
      this.box(hull, side * .965, 1.10, -.29, .035, .22, .48, PATCH, .11);
      this.box(hull, side * .955, 1.34, .02, .025, .055, .25, EDGE);
      this.box(hull, side * 1.0, 1.56, .96, .10, .12, .15, DARK);
      const brokenLamp = front >= 3 && side === crumpledSide;
      this.box(hull, side * .70, 1.07 - (front >= 2 ? (side === crumpledSide ? .14 : .05) * (front - 1) : 0), 2.57 - front * .09,
        .31, .14, .035, p.collapse > .5 || brokenLamp ? DARK : AMBER, 0, p.collapse < .5 && !brokenLamp);
      this.box(hull, side * .82, 1.16, -2.68 + rear * .10, .14, .14, .04, p.collapse > .5 ? DARK : RED, 0, p.collapse < .5);
      this.box(hull, side * .83, 1.45, -.58, .07, 1.03, .07, DARK);
      this.box(hull, side * .94, .83 - (front >= 2 && side === crumpledSide ? front * .065 : 0), 1.74, .13, .14, .92, EDGE,
        front >= 2 ? front * .07 : 0, false, front >= 2 && side === crumpledSide ? side * .22 : 0);
      this.box(hull, side * .94, .83, -1.87, .13, .14, .92, EDGE);
      this.box(hull, side * .96, .80, -.05, .11, .07, 2.40, DARK);
    }
    this.box(hull, 0, 1.95, -.58, 1.74, .07, .07, DARK);
    // Driver seat, rear bench and coarse wheel/control connection, no cockpit UI.
    this.box(hull, -.45, .98, .55, .49, .12, .51, DARK);
    this.box(hull, -.45, 1.25, .28, .47, .49, .11, DARK, -.09);
    this.box(hull, .47, .98, .55, .45, .12, .51, DARK);
    this.box(hull, .47, 1.25, .28, .43, .49, .11, DARK, -.09);
    this.box(hull, -.45, 1.34, .93, .30, .04, .20, EDGE, -.25);
    this.box(hull, -.45, 1.20, 1.00, .055, .27, .055, DARK, -.30);
    // Four independently sampled contacts. The axle rotates around X and front axles steer.
    for (let wheel = 0; wheel < 4; wheel++) {
      const side = wheel % 2 ? 1 : -1, isFront = wheel < 2;
      const foot = this.feet[wheel]!;
      this.localPoint(p, side * .94, 0, isFront ? 1.77 : -1.85, foot);
      if (now - foot.sampleAt >= 100 || !Number.isFinite(foot.sampleAt)) {
        const ground = heightAt(foot.x, foot.z); foot.ground = Number.isFinite(ground) ? ground : p.y; foot.sampleAt = now;
      }
      foot.y = Math.max(p.y - .25, Math.min(p.y + .25, foot.ground)) + .45 - p.collapse * .04;
      const steer = isFront ? (p.steering ?? 0) * .50 : 0;
      const wobble = stage >= 2 && wheel === (p.damageSeed ?? 0) % 4 ? Math.sin(p.phase) * .025 * stage : 0;
      this.addWheel(foot, .28, .90, .90, p.yaw + steer + wobble, p.phase, DARK);
      this.a.x = foot.x + Math.cos(p.yaw + steer) * side * .15; this.a.y = foot.y; this.a.z = foot.z - Math.sin(p.yaw + steer) * side * .15;
      this.addWheel(this.a, .045, .46, .46, p.yaw + steer + wobble, p.phase, EDGE);
      // Asymmetric hub spoke makes rotation readable on the shared wheel resource.
      this.worldBox(this.a.x, this.a.y, this.a.z, .05, .36, .075, p.yaw + steer + wobble, p.phase, RUST);
    }
    const mountPoint = { x: 0, y: 0, z: 0 }; this.localPoint(p, 0, 2.05 - p.collapse * .18, -1.25, mountPoint);
    const turret = { ...p, ...mountPoint, yaw: p.turretYaw, hullPitch: 0, hullRoll: 0 };
    this.box(turret, 0, -.68, 0, .22, 1.15, .22, DARK);
    this.box(turret, 0, -.96, 0, 1.10, .11, 1.10, EDGE);
    this.box(turret, 0, -.17, 0, .83, .32, .86, PATCH);
    this.box(turret, 0, .13, -.41, .79, .59, .45, DARK, -p.aimPitch);
    for (const side of [-1, 1]) {
      this.box(turret, side * .56, -.02, -.31, .16, .51, .59, RUST);
      this.box(turret, side * .29, -.15, -.71, .07, .07, .30, EDGE);
      this.box(turret, side * .49, .08, -.44, .25, .49, .51, PATCH);
      for (const level of [.12, .46]) {
        const x = side * .23, cp = Math.cos(p.aimPitch), sp = Math.sin(p.aimPitch), recoil = p.recoil * .07;
        this.box(turret, x, level * cp + .29 * sp, .29 * cp - level * sp - recoil, .12, .12, 1.98, DARK, -p.aimPitch);
        this.box(turret, x, level * cp + 1.23 * sp, 1.23 * cp - level * sp - recoil, .18, .18, .14, EDGE, -p.aimPitch);
        this.box(turret, x, level * cp + 1.307 * sp, 1.307 * cp - level * sp - recoil, .10, .10, .016, DARK, -p.aimPitch);
      }
    }
    this.box(turret, 0, -.57, -.70, .49, .12, .47, DARK);
    this.box(turret, 0, -.35, -.97, .47, .48, .09, DARK, -.1);
    if (stage >= 2) {
      // High-contrast scratches/creases remain attached to the impacted panel.
      if (front) for (let i = 0; i < 3; i++) this.box(hull, -.52 + i * .48, 1.285 + (front === 2 ? .20 : .36), 2.01 - front * .04,
        .035, .028, .69, DARK, front === 2 ? .48 : .86, false, bend * front);
      if (zone === 2 || zone === 3) this.box(hull, (zone === 2 ? -1 : 1) * (1.0 + stage * .02), 1.18, .53, .026, .06, .61, DARK, -.22);
      if (rear) this.box(hull, .24, 1.33 - rear * .07, -2.70 + rear * .10, .75, .045, .04, DARK, .14);
      // A narrow cracked windshield crossbar preserves the driver's visible face.
      this.box(hull, .23, 1.61, 1.10, .025, .36, .025, DARK, .36);
    }
  }

  private aimedBox(p: MachinePose, x: number, y: number, z: number, forward: number,
    sx: number, sy: number, sz: number, tint: CombatTint, pitch: number): void {
    this.box(p, x, y + Math.sin(pitch) * forward, z + Math.cos(pitch) * forward, sx, sy, sz, tint, -pitch);
  }

  private localPoint(p: CombatPoint & { yaw: number; hullPitch?: number; hullRoll?: number }, x: number, y: number, z: number, out: CombatPoint): void {
    const roll = p.hullRoll ?? 0, pitch = p.hullPitch ?? 0;
    const rx = x * Math.cos(roll) - y * Math.sin(roll), ry = x * Math.sin(roll) + y * Math.cos(roll);
    const py = ry * Math.cos(pitch) - z * Math.sin(pitch), pz = ry * Math.sin(pitch) + z * Math.cos(pitch);
    out.x = p.x + Math.cos(p.yaw) * rx + Math.sin(p.yaw) * pz;
    out.y = p.y + py; out.z = p.z - Math.sin(p.yaw) * rx + Math.cos(p.yaw) * pz;
  }
  private box(p: CombatPoint & { yaw: number; hullPitch?: number; hullRoll?: number }, x: number, y: number, z: number, sx: number, sy: number, sz: number,
    tint: CombatTint, pitch = 0, glow = false, roll = 0, yawOffset = 0): void {
    this.localPoint(p, x, y, z, this.b);
    this.worldBox(this.b.x, this.b.y, this.b.z, sx, sy, sz, p.yaw + yawOffset, pitch + (p.hullPitch ?? 0), tint, glow, (p.hullRoll ?? 0) + roll);
  }
  private worldBox(x: number, y: number, z: number, sx: number, sy: number, sz: number, yaw: number, pitch: number, tint: CombatTint, glow = false, roll = 0): void {
    const data = glow ? this.glow : this.metal, index = glow ? this.glowCount++ : this.metalCount++;
    writePart(data, index * 20, x, y, z, sx, sy, sz, yaw, pitch, tint, roll);
  }
  private addWheel(point: CombatPoint, sx: number, sy: number, sz: number, yaw: number, roll: number, tint: CombatTint): void {
    writePart(this.wheels, this.wheelCount++ * 20, point.x, point.y, point.z, sx, sy, sz, yaw, roll, tint);
  }
  private segment(a: CombatPoint, b: CombatPoint, width: number, depth: number, tint: CombatTint): void {
    const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
    this.worldBox((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2, width, depth,
      Math.hypot(dx, dy, dz), Math.atan2(dx, dz), -Math.atan2(dy, Math.hypot(dx, dz)), tint);
  }
}

export function writePart(data: Float32Array, offset: number, x: number, y: number, z: number,
  sx: number, sy: number, sz: number, yaw: number, pitch: number, tint: CombatTint, roll = 0): void {
  const cy = Math.cos(yaw), syaw = Math.sin(yaw), cp = Math.cos(pitch), sp = Math.sin(pitch);
  data[offset] = cy * sx; data[offset + 1] = 0; data[offset + 2] = -syaw * sx; data[offset + 3] = 0;
  data[offset + 4] = syaw * sp * sy; data[offset + 5] = cp * sy; data[offset + 6] = cy * sp * sy; data[offset + 7] = 0;
  data[offset + 8] = syaw * cp * sz; data[offset + 9] = -sp * sz; data[offset + 10] = cy * cp * sz; data[offset + 11] = 0;
  if (roll) {
    const cr = Math.cos(roll), sr = Math.sin(roll);
    for (let row = 0; row < 3; row++) {
      const a = data[offset + row]! / sx, b = data[offset + 4 + row]! / sy;
      data[offset + row] = (a * cr + b * sr) * sx;
      data[offset + 4 + row] = (b * cr - a * sr) * sy;
    }
  }
  data[offset + 12] = x; data[offset + 13] = y; data[offset + 14] = z; data[offset + 15] = 1;
  data.set(tint, offset + 16);
}

export function houndSensorPresentation(p: MachinePose, now: number): { state: "SEARCHING" | "TRACKING" | "LOCKED" | "DAMAGED"; yaw: number; pitch: number; led: CombatTint; lit: boolean } {
  const damaged = p.collapse > .35 || p.damaged > .7;
  const locked = p.state === 4 || p.recoil > .02;
  const tracking = locked || p.state === 2 || p.state === 3;
  const state = damaged ? "DAMAGED" : locked ? "LOCKED" : tracking ? "TRACKING" : "SEARCHING";
  const time = now / 1000;
  // A sine held briefly near its end points gives a deliberate sweep-and-observe cadence.
  const sweep = Math.max(-.72, Math.min(.72, Math.sin(time * .76 + p.phase * .025) * .91));
  const lit = damaged ? Math.sin(time * 23) * Math.sin(time * 9.7) > .36 : locked || Math.sin(time * (tracking ? 13 : 3.0)) > -.18;
  return { state, yaw: tracking || damaged ? p.turretYaw : p.yaw + sweep,
    pitch: tracking ? p.aimPitch : Math.sin(time * .48) * .055,
    led: damaged || locked || tracking && Math.sin(time * 13) > 0 ? RED : AMBER, lit };
}
